import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import { EXCHANGE_GOOGLE_ANALYTICS_CODE } from '../../graphql';
import { getFullPath } from '../../utils/pathUtils';
import {
  GA_CONNECT_FAILED,
  GA_PROPERTIES,
  GaExchange,
  stashPending,
} from '../../utils/analyticsConnect';

/**
 * /auth/google-analytics/callback — where the Google sign-in popup lands. It
 * trades the code with the API for the person's GA4 properties and hands them
 * to the window that opened it, which connects the site's one (or lets the
 * person pick). Nothing is connected from here.
 */
const GoogleAnalyticsCallback: React.FC = () => {
  const [exchange] = useMutation<{ exchangeGoogleAnalyticsCode: GaExchange }>(
    EXCHANGE_GOOGLE_ANALYTICS_CODE
  );
  const [status, setStatus] = useState<'working' | 'done' | 'error'>('working');
  const [message, setMessage] = useState('Finishing the Google sign-in…');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // React StrictMode runs effects twice in development
    ran.current = true;

    const closeSoon = (ms: number) => {
      setTimeout(() => {
        if (window.opener) window.close();
        else window.location.href = getFullPath('/content?view=analytics');
      }, ms);
    };

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const denied = params.get('error');
      try {
        if (denied) {
          throw new Error(
            denied === 'access_denied'
              ? 'You didn’t give tinycms access, so nothing was connected.'
              : `Google didn’t finish the sign-in (${denied}).`
          );
        }
        if (!code || !state)
          throw new Error('Google didn’t send a sign-in code. Please try again.');
        const { data } = await exchange({ variables: { code, state } });
        const result = data?.exchangeGoogleAnalyticsCode;
        if (!result) throw new Error('The Google sign-in couldn’t be completed.');

        stashPending(result);
        if (window.opener) {
          window.opener.postMessage({ type: GA_PROPERTIES, result }, window.location.origin);
        }
        setStatus('done');
        setMessage(
          result.properties.length
            ? 'Signed in to Google. You can close this window.'
            : 'Signed in, but that Google account has no Google Analytics properties yet.'
        );
        closeSoon(1200);
      } catch (err) {
        const text =
          err instanceof Error ? err.message : 'The Google sign-in couldn’t be completed.';
        setStatus('error');
        setMessage(text);
        if (window.opener) {
          window.opener.postMessage(
            { type: GA_CONNECT_FAILED, message: text },
            window.location.origin
          );
        }
        closeSoon(6000);
      }
    };
    void run();
  }, [exchange]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center max-w-xl mx-auto">
      {status === 'working' && <Loader2 className="w-9 h-9 mb-4 text-primary animate-spin" />}
      <h6 className="text-xl font-semibold text-white mb-2">
        {status === 'working' && 'Connecting Google Analytics'}
        {status === 'done' && 'Google Analytics signed in'}
        {status === 'error' && 'Google Analytics could not be connected'}
      </h6>
      <p className="mt-2 text-cmp-ink-2">{message}</p>
      {status !== 'working' && (
        <p className="mt-4 text-sm text-cmp-ink-3">This window closes on its own.</p>
      )}
    </div>
  );
};

export default GoogleAnalyticsCallback;
